import { useMemo } from 'react'
import { Box } from '@mui/material'
import { BaseTable } from 'Features/Table/BaseTable.view'
import { ExtendedGridColDef } from 'Features/Table/BulkUpdateModal'
import {
  SheduleTableProps,
  SchedulerResourceSwitchingRowType,
} from './Scheduler.types'

/**
 * Equipment-switching times between lines that share one physical set of
 * equipment.
 *
 * Two things a planner needs to know, and neither is obvious from the columns
 * alone, so both are on the column tooltips:
 *
 * - Entries are **directed**. `BTL01 → BTL02` and `BTL02 → BTL01` are separate
 *   rows and may carry different durations.
 * - Listing two lines here also makes them **mutually exclusive** — one set of
 *   equipment cannot be in two places, so the scheduler will never run them at
 *   the same time. That is a bigger change to a schedule than the switching
 *   time itself, and it applies even where the time is 0.
 *
 * A row may also name a **Resource Constraint** the move consumes while it
 * runs, with an amount — the same two columns, the same rules and the same
 * reporting as the format-changeover table. The exclusion between THIS pair is
 * already covered by the two lines sharing a group; naming a resource is what
 * keeps everything else that needs it out of the move's hours, and what puts
 * those hours on the resource-usage charts.
 */

const dataConfig = {
  required: ['previous_resource', 'next_resource', 'switching_time'],
  optional: ['resource_constraint', 'resource_amount'],
}

type SchedulerResourceSwitchingTableProps =
  SheduleTableProps<SchedulerResourceSwitchingRowType> & {
    /** Real line codes from the packaging routes, so the pickers can't produce
     *  a name the engine will reject as an unknown workstation. */
    lineOptions: string[]
    /** Resource-constraint names a switch can consume. */
    resourceOptions: string[]
  }

/** Non-negative number, or blank. */
const nonNegative = (val: unknown): boolean => {
  if (val === null || val === undefined || val === '') return true
  const n = Number(val)
  return Number.isFinite(n) && n >= 0
}

export const SchedulerResourceSwitchingTable = ({
  data,
  isLoading,
  updateTableData,
  uploadScheduleData,
  isUploading,
  isSaving,
  updateRowsStatus,
  predicate,
  deleteTableData,
  isDeleting,
  lineOptions,
  resourceOptions,
}: SchedulerResourceSwitchingTableProps) => {
  const handleDataUpdate = (
    changedRows: Record<number, SchedulerResourceSwitchingRowType>
  ) => {
    updateTableData(changedRows)
  }

  /** The other half of the pair as it currently stands on the row being
   *  edited, so a self-pair can be rejected at entry. Reads the in-flight edit
   *  where there is one, falling back to the committed row. */
  const otherHalf = (
    params: { row: SchedulerResourceSwitchingRowType; field?: string },
    field: 'previous_resource' | 'next_resource'
  ): string => String(params.row?.[field] ?? '')

  const columns: ExtendedGridColDef[] = useMemo(
    () => [
      {
        field: 'previous_resource',
        headerName: 'From Resource',
        type: 'singleSelect',
        valueOptions: lineOptions,
        flex: 1,
        // Not editable: the grid already lists every ordered pair, so the only
        // thing to change on a row is its time. Editing a pair in place would
        // create a duplicate slot rather than move the row.
        editable: false,
        description:
          'The line the equipment moves FROM. Entries are directed — the reverse move is its own row and can have a different time.',
        preProcessEditCellProps: (params) => {
          const value = String(params.props.value ?? '')
          const to = otherHalf(
            params as unknown as { row: SchedulerResourceSwitchingRowType },
            'next_resource'
          )
          // A line switching to itself is not a transition; the engine drops
          // such a row with a warning, so block it here instead.
          return {
            ...params.props,
            error: Boolean(value) && value === to,
          }
        },
      },
      {
        field: 'next_resource',
        headerName: 'To Resource',
        type: 'singleSelect',
        valueOptions: lineOptions,
        flex: 1,
        editable: false,
        description:
          'The line the equipment moves TO. Giving this pair a non-zero time also stops the two lines running at the same time — there is only one set of equipment.',
        preProcessEditCellProps: (params) => {
          const value = String(params.props.value ?? '')
          const from = otherHalf(
            params as unknown as { row: SchedulerResourceSwitchingRowType },
            'previous_resource'
          )
          return {
            ...params.props,
            error: Boolean(value) && value === from,
          }
        },
      },
      {
        field: 'switching_time',
        headerName: 'Switching Time (hrs)',
        type: 'number',
        flex: 1,
        editable: true,
        description:
          'Hours the equipment change takes. 0 (the default on every pair) means these two lines share no equipment: nothing is charged and they may run at the same time. Any value above 0 declares shared equipment — the time is applied AND the two lines can never run concurrently. It is a minimum separation rather than a flat penalty, so a move with idle time either side costs nothing.',
        preProcessEditCellProps: (params) => {
          const val = params.props.value
          const n = Number(val)
          const invalid =
            val === null ||
            val === undefined ||
            val === '' ||
            !Number.isFinite(n) ||
            n < 0
          return { ...params.props, error: invalid }
        },
      },
      {
        // The reason this column exists: moving the equipment is not free to
        // the rest of the plant. Naming a resource here holds it for the whole
        // move, so anything else needing it waits. The two lines of this pair
        // are already mutually exclusive through their group — this is about
        // everything else.
        field: 'resource_constraint',
        headerName: 'Resource Constraint',
        type: 'singleSelect',
        valueOptions: resourceOptions,
        flex: 1,
        editable: true,
        description:
          'Resource the equipment change consumes while it runs, from Resource & Material Constraints. Held for the whole move, so anything else needing it must wait — and the hours appear as usage on the Resource Usage tab. Leave blank if the move consumes nothing. Ignored on a pair whose switching time is 0, which is not a move at all.',
      },
      {
        field: 'resource_amount',
        headerName: 'Resource Amount',
        type: 'number',
        flex: 1,
        editable: true,
        description:
          'Units of the resource consumed per hour, checked against its capacity exactly as a production run is. Blank means 1 — a single unit, which fully occupies a capacity-1 resource. An amount above the resource capacity is rejected by the engine, since such a switch could never be scheduled.',
        preProcessEditCellProps: (params) => ({
          ...params.props,
          error: !nonNegative(params.props.value),
        }),
      },
    ],
    [lineOptions, resourceOptions]
  )

  return (
    <Box>
      <BaseTable
        // Each user's saved view of this table (Save as my default).
        layoutKey="ResourceSwitching"
        data={data ?? []}
        columns={columns}
        isLoading={isLoading}
        updateTableData={handleDataUpdate}
        uploadData={uploadScheduleData}
        isUploading={isUploading}
        isSaving={isSaving}
        checkBoxSelection={true}
        showTableToolBar={true}
        downloadFileName="SchedulerResourceSwitching"
        onDeleteRows={deleteTableData}
        isDeleting={isDeleting}
        multiStageUpload={{
          uploadFile: uploadScheduleData,
          isUploading: isUploading,
          columns: columns,
          updateRowsStatus: updateRowsStatus,
          originalData: data,
          predicate: predicate,
          dataConfig: dataConfig,
        }}
        initialState={{
          pagination: { paginationModel: { pageSize: 100 } },
        }}
        sx={{
          maxWidth: 'calc(100vw - 70px)',
          '& .MuiDataGrid-cell--editable': {
            backgroundColor: '#CBD5E1',
          },
          '& .MuiBox-root': {
            padding: '0px !important',
          },
        }}
      />
    </Box>
  )
}
